import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, Download, Film } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PILLARS, POSTS, Pillar } from "@/data/posts";
import { Reveal } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { INSTAGRAM_HANDLE } from "@/lib/site";

const COVERS = [
  { src: "/assets/cover-quem-somos.png", label: "Quem somos" },
  { src: "/assets/cover-servicos.png", label: "Serviços" },
  { src: "/assets/cover-seguranca.png", label: "Segurança" },
  { src: "/assets/cover-antes-depois.png", label: "Antes/Depois" },
  { src: "/assets/cover-orcamento.png", label: "Orçamento" },
];

const FILTERS: ("Todos" | Pillar)[] = ["Todos", "Autoridade", "Prova", "Conversão"];

const copy = async (text: string, what: string) => {
  await navigator.clipboard.writeText(text);
  toast.success(`${what} copiada. Cole direto no Instagram.`);
};

const PostCard = ({ post, index }: { post: (typeof POSTS)[number]; index: number }) => {
  const [showReels, setShowReels] = useState(false);
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <article data-testid={`post-card-${post.id}`} className="border border-white/10 bg-[#0A0A0A]">
        <div className="group relative overflow-hidden">
          <SafeImage src={post.art} alt={`Arte do post ${post.title}`} label={`Arte em geração — ${post.title}`} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <span className="absolute left-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-krono backdrop-blur-md">
            {post.pillar} · Post {String(post.id).padStart(2, "0")}
          </span>
        </div>
        <div className="p-6">
          <h3 className="font-display text-xl font-semibold">{post.title}</h3>
          <p className="mt-4 max-h-40 overflow-y-auto whitespace-pre-line border border-white/10 bg-black p-4 text-xs leading-relaxed text-white/70">
            {post.caption}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => copy(post.caption, "Legenda")} data-testid={`post-${post.id}-copy-caption`} className="inline-flex items-center gap-2 bg-krono px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors hover:bg-krono-hover">
              <Copy className="h-3.5 w-3.5" /> Copiar legenda
            </button>
            <a href={post.art} download={`krono-post-${String(post.id).padStart(2, "0")}.png`} data-testid={`post-${post.id}-download-art`} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/80 transition-colors hover:border-krono hover:text-krono">
              <Download className="h-3.5 w-3.5" /> Arte 1080×1350
            </a>
            {post.reels && (
              <button onClick={() => setShowReels(!showReels)} data-testid={`post-${post.id}-toggle-reels`} className="inline-flex items-center gap-2 border border-krono px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-krono transition-colors hover:bg-krono hover:text-black">
                <Film className="h-3.5 w-3.5" /> {showReels ? "Ocultar roteiro" : "Roteiro Reels"}
              </button>
            )}
          </div>
          {showReels && post.reels && (
            <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`post-${post.id}-reels-script`} className="mt-4 whitespace-pre-line border-l-2 border-krono bg-black p-4 text-xs leading-relaxed text-white/70">
              {post.reels}
            </motion.p>
          )}
        </div>
      </article>
    </Reveal>
  );
};

export default function ContentPackPage({ user }: { user?: { name?: string; email?: string } }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("Todos");
  const visible = filter === "Todos" ? POSTS : POSTS.filter((p) => p.pillar === filter);

  const logout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    window.location.href = "/";
  };

  return (
    <div data-testid="content-pack-page" className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="mb-8 flex items-center justify-between gap-4">
            <Link to="/" data-testid="pack-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-krono">
              <ArrowLeft className="h-4 w-4" /> Voltar ao site
            </Link>
            <div className="flex items-center gap-4">
              {user?.email && (
                <span data-testid="logged-user-email" className="hidden font-mono text-xs text-white/50 sm:inline">
                  {user.email}
                </span>
              )}
              <button
                onClick={logout}
                data-testid="logout-button"
                className="border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-krono hover:text-krono"
              >
                Sair
              </button>
            </div>
          </div>
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-krono">[ {INSTAGRAM_HANDLE} — Mês 01 ]</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl">
            Pacote de conteúdo — 12 posts
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/60">
            3 posts por semana, divididos em 3 pilares. Cada post tem arte pronta (1080×1350), legenda com CTA e hashtags de SP, e roteiro de Reels quando aplicável.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-4 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <div key={p.name} data-testid={`pillar-${p.name.toLowerCase()}`} className="border border-white/10 p-6">
              <span className="font-mono text-sm text-krono">[ 0{i + 1} ]</span>
              <h2 className="mt-2 font-display text-lg font-semibold">{p.name} — 4 posts/mês</h2>
              <p className="mt-2 text-xs leading-relaxed text-white/60">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap gap-2" data-testid="pack-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
              className={`px-5 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${filter === f ? "bg-krono text-black" : "border border-white/20 text-white/70 hover:border-krono hover:text-krono"}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((post, i) => (
            <PostCard key={post.id} post={post} index={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-krono">[ Destaques do perfil ]</p>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Capas de destaque</h2>
          <div className="mt-10 grid grid-cols-3 gap-4 sm:grid-cols-5">
            {COVERS.map((c) => (
              <div key={c.src} className="group" data-testid={`cover-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div className="overflow-hidden border border-white/10">
                  <SafeImage src={c.src} alt={`Capa de destaque ${c.label}`} label={c.label} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex items-center gap-3 border border-white/10 p-4">
            <Check className="h-4 w-4 shrink-0 text-krono" />
            <p className="text-xs leading-relaxed text-white/60">
              Todas as artes estão em <span className="font-mono text-krono">/assets</span> e podem ser baixadas. Os posts 10 (depoimento) e o portfólio do site usam templates prontos para receber fotos e frases reais das primeiras obras.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
